import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import {
  ChevronRight, ChevronLeft, ChevronDown, Info,
  Users, Zap, FileText, BarChart3, Calculator, Mail,
  Layers, Shield, Building2, Monitor, Wrench, DollarSign,
  Warehouse, Plane, Hotel, Home, Heart, Ticket, UtensilsCrossed, Lock,
  PlayCircle
} from 'lucide-react';

/* ───────────────────────────────────────────
   Per-page section navigation configs
   ─────────────────────────────────────────── */
/* Grouped "This Page" sections for Homepage */
const HOME_SECTION_GROUPS = [
  {
    key: 'overview',
    label: 'Overview',
    items: [
      { id: 'detection-delayed-section', label: 'The Platform' },
      { id: 'definition-banner-section', label: 'Four Pillars' },
      { id: 'missing-layer-section', label: 'The Gap' },
      { id: 'why-now-section', label: 'Why Now' },
    ],
  },
  {
    key: 'how-it-works',
    label: 'How It Works',
    items: [
      { id: 'how-it-works-section', label: 'How It Works' },
      { id: 'engine-tags-section', label: 'Engine Tags' },
      { id: 'command-center-section', label: 'Command Center' },
      { id: 'operational-visibility-section-content', label: 'Capabilities' },
      { id: 'industries-section', label: 'Industries' },
      { id: 'deployment-scenarios-section', label: 'Deployment' },
      { id: 'outcomes-section', label: 'Impact' },
      { id: 'operational-leaders-section', label: 'Leaders' },
    ],
  },
];

const PAGE_SECTIONS = {
  '/': [
    { id: 'detection-delayed-section', label: 'The Platform' },
    { id: 'definition-banner-section', label: 'Four Pillars' },
    { id: 'missing-layer-section', label: 'The Gap' },
    { id: 'why-now-section', label: 'Why Now' },
    { id: 'how-it-works-section', label: 'How It Works' },
    { id: 'engine-tags-section', label: 'Engine Tags' },
    { id: 'command-center-section', label: 'Command Center' },
    { id: 'operational-visibility-section-content', label: 'Capabilities' },
    { id: 'industries-section', label: 'Industries' },
    { id: 'deployment-scenarios-section', label: 'Deployment' },
    { id: 'outcomes-section', label: 'Impact' },
    { id: 'operational-leaders-section', label: 'Leaders' },
    { id: 'cta-section', label: 'Get Started' },
  ],
  '/implementation': [
    { id: 'risk-deployment-models-section', label: 'Risk Models' },
    { id: 'deployment-overview-section', label: 'Deployment' },
    { id: 'deployment-diagram-section', label: 'Facility Layout' },
    { id: 'human-presence-section', label: 'Human Presence' },
    { id: 'hazard-response-section', label: 'Hazard Response' },
    { id: 'operational-visibility-facility-section', label: 'Heatmap' },
    { id: 'impl-command-center-section', label: 'Command Center' },
    { id: 'impl-incident-intelligence-section', label: 'Analytics' },
    { id: 'deployment-calculator-section', label: 'Calculator' },
  ],
  '/operational-risk': [
    { id: 'risk-stats-section', label: 'Risk Data' },
    { id: 'hazard-distribution-section', label: 'Distribution' },
    { id: 'facility-hazards-section', label: 'Facility Hazards' },
    { id: 'probability-models-section', label: 'Probability' },
    { id: 'risk-insight-section', label: 'Risk Insight' },
    { id: 'visibility-gap-section', label: 'Visibility Gap' },
    { id: 'deployment-estimator-section', label: 'Estimator' },
    { id: 'exposure-calculator-section', label: 'Calculator' },
    { id: 'sources-section', label: 'Sources' },
  ],
  '/operational-experience': [
    { id: 'adoption-section', label: 'Adoption' },
    { id: 'hidden-gap-section', label: 'Hidden Gap' },
    { id: 'experience-layer-section', label: 'Experience Layer' },
    { id: 'sector-examples-section', label: 'Sectors' },
    { id: 'why-matters-section', label: 'Why It Matters' },
  ],
  '/contact': [],
  '/security': [],
};

/* ───────────────────────────────────────────
   Platform cross-navigation (4 pillars)
   ─────────────────────────────────────────── */
const PLATFORM_PILLARS = [
  { label: 'Connect', icon: Users, path: '/operational-experience', pillar: 'connect' },
  { label: 'Respond', icon: Zap, path: '/implementation#hazard-response-section', pillar: 'respond' },
  { label: 'Record', icon: FileText, path: '/implementation#impl-command-center-section', pillar: 'record' },
  { label: 'Intelligence', icon: BarChart3, path: '/operational-risk#intelligence-layer-section', pillar: 'intelligence' },
];

/* ───────────────────────────────────────────
   Explore navigation (migrated from old SiteNav)
   ─────────────────────────────────────────── */
const EXPLORE_GROUPS = [
  {
    key: 'platform',
    label: 'Platform',
    icon: Layers,
    items: [
      { path: '/platform', label: 'Platform Overview', icon: Layers },
      { path: '/technology', label: 'Technology', icon: Monitor },
      { path: '/enterprise', label: 'Enterprise', icon: Building2 },
      { path: '/why-tapelligence', label: 'Why Tapelligence', icon: Zap },
    ],
  },
  {
    key: 'industries',
    label: 'Industries',
    icon: Users,
    items: [
      { path: '/industries', label: 'Industry Overview', icon: Users },
      { path: '/warehouse', label: 'Warehouse & Retail', icon: Warehouse },
      { path: '/airports', label: 'Airports', icon: Plane },
      { path: '/casino', label: 'Casino & Gaming', icon: Hotel },
      { path: '/hotels', label: 'Hotels & Resorts', icon: Home },
      { path: '/healthcare', label: 'Healthcare', icon: Heart },
      { path: '/stadiums', label: 'Stadiums & Events', icon: Ticket },
      { path: '/real-estate', label: 'Real Estate', icon: Building2 },
      { path: '/restaurants-qsr', label: 'Restaurants & QSR', icon: UtensilsCrossed },
    ],
  },
  {
    key: 'tools',
    label: 'Tools & Calculators',
    icon: Wrench,
    items: [
      { path: '/exposure', label: 'Exposure Calculator', icon: DollarSign },
      { path: '/operational-risk', label: 'Risk-Based Deployment', icon: Shield },
      { path: '/implementation', label: 'Implementation', icon: Wrench },
      { path: '/operational-experience', label: 'Operational Experience', icon: Zap },
      { path: '/operational-impact', label: 'Operational Impact', icon: Building2 },
    ],
  },
  {
    key: 'research',
    label: 'Research',
    icon: FileText,
    items: [
      { path: '/operational-risk-data', label: 'Risk Research', icon: FileText },
    ],
  },
];

const SideTab = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasAutoCollapsed, setHasAutoCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedPageGroups, setExpandedPageGroups] = useState({ overview: true, 'how-it-works': true });
  const panelRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  const currentPath = location.pathname.replace(`/${language}`, '') || '/';
  const pageSections = PAGE_SECTIONS[currentPath] || PAGE_SECTIONS['/'];
  const getPath = (p) => `/${language}${p}`;
  const isActivePage = (path) => currentPath === path || currentPath === `${path}/`;

  // Auto-open on first load, then auto-collapse after 5s
  useEffect(() => {
    if (!hasAutoCollapsed) {
      setIsOpen(true);
      const timer = setTimeout(() => {
        setIsOpen(false);
        setHasAutoCollapsed(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [hasAutoCollapsed]);

  // Close panel on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Track active section via IntersectionObserver
  useEffect(() => {
    const observers = [];
    const callback = (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.getAttribute('data-testid') || entry.target.id);
        }
      }
    };
    const observer = new IntersectionObserver(callback, {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0,
    });
    for (const s of pageSections) {
      const el = document.querySelector(`[data-testid="${s.id}"]`);
      if (el) { observer.observe(el); observers.push(el); }
    }
    return () => { for (const el of observers) observer.unobserve(el); };
  }, [currentPath, pageSections]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape' && isOpen) setIsOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const scrollToSection = useCallback((sectionId) => {
    const el = document.querySelector(`[data-testid="${sectionId}"]`);
    setIsOpen(false);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    if (PAGE_SECTIONS[currentPath]) return;
    // Page has no own list: the items are homepage sections, so go there and scroll once it renders
    navigate(`/${language}`);
    let tries = 0;
    const poll = setInterval(() => {
      const target = document.querySelector(`[data-testid="${sectionId}"]`);
      if (target || ++tries > 40) {
        clearInterval(poll);
        if (target) {
          const go = () => target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setTimeout(go, 300);
          // Correct once more if the page grew while scrolling
          setTimeout(() => { if (Math.abs(target.getBoundingClientRect().top) > 60) go(); }, 1500);
        }
      }
    }, 100);
  }, [currentPath, language, navigate]);

  const navigateTo = useCallback((path) => {
    const [route, hash] = path.split('#');
    navigate(`/${language}${route}`);
    setIsOpen(false);
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${hash}"]`) || document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
    }
  }, [language, navigate]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const togglePageGroup = (key) => {
    setExpandedPageGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Auto-expand group containing active page
  useEffect(() => {
    const newState = {};
    for (const group of EXPLORE_GROUPS) {
      if (group.items.some(item => isActivePage(item.path))) {
        newState[group.key] = true;
      }
    }
    setExpandedGroups(newState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[39] lg:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Floating button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed z-[41] top-1/2 -translate-y-1/2 transition-all duration-300 ease-in-out flex items-center gap-1 py-2.5 rounded-r-xl shadow-lg ${
          isOpen
            ? 'pl-1.5 pr-2 bg-white border border-l-0 border-slate-200'
            : 'pl-2.5 pr-2 bg-[#0077FF] text-white hover:bg-[#0066DD]'
        }`}
        style={{ left: isOpen ? '272px' : '0px' }}
        data-testid="side-tab-button"
        aria-label={isOpen ? 'Close navigation panel' : 'Open navigation panel'}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <ChevronLeft className="w-5 h-5 text-slate-500" />
        ) : (
          <>
            <Info className="w-5 h-5" />
            <ChevronRight className="w-4 h-4" />
          </>
        )}
      </button>

      {/* Navigation panel */}
      <nav
        ref={panelRef}
        className={`fixed left-0 top-0 h-full w-[272px] z-[40] bg-white border-r border-slate-200 shadow-xl transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        role="navigation"
        aria-label="Site navigation panel"
        data-testid="side-tab-panel"
      >
        <div className="flex flex-col h-full pt-[80px] pb-4 overflow-y-auto scrollbar-thin">

          {/* ════════ HOME BUTTON ════════ */}
          <div className="px-4 mb-2">
            <Link
              to={getPath('/')}
              onClick={() => setIsOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 ${
                currentPath === '/'
                  ? 'text-[#0077FF] bg-[#0077FF]/8'
                  : 'text-slate-700 hover:text-[#0077FF] hover:bg-slate-50'
              }`}
              data-testid="side-tab-home"
            >
              <Home className={`w-4 h-4 flex-shrink-0 ${currentPath === '/' ? 'text-[#0077FF]' : 'text-slate-500'}`} />
              {t('nav.home')}
            </Link>
          </div>

          <div className="mx-4 border-t border-slate-100 mb-2" />

          {/* ════════ ZONE 1 — PLATFORM PILLARS ════════ */}
          <div className="px-4 mb-3">
            <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-2.5 px-1">
              Platform
            </p>
            <div className="grid grid-cols-2 gap-1">
              {PLATFORM_PILLARS.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.pillar}
                    onClick={() => navigateTo(item.path)}
                    className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-xs font-medium text-slate-600 hover:bg-[#0077FF]/6 hover:text-[#0077FF] transition-colors duration-150"
                    data-testid={`side-tab-platform-${item.pillar}`}
                  >
                    <Icon className="w-3.5 h-3.5 text-[#0077FF] flex-shrink-0" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ════════ ZONE 2 — EXPLORE (migrated from old sidebar) ════════ */}
          <div className="mx-4 border-t border-slate-100 mb-2" />
          <div className="px-4 mb-3">
            <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-2.5 px-1">
              Explore
            </p>
            <div className="space-y-0.5">
              {EXPLORE_GROUPS.map((group) => {
                const GroupIcon = group.icon;
                const isExpanded = expandedGroups[group.key];
                const hasActiveChild = group.items.some(item => isActivePage(item.path));
                return (
                  <div key={group.key}>
                    <button
                      onClick={() => toggleGroup(group.key)}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold transition-colors duration-150 ${
                        hasActiveChild
                          ? 'text-[#0077FF] bg-[#0077FF]/6'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                      }`}
                      data-testid={`side-tab-group-${group.key}`}
                    >
                      <GroupIcon className={`w-3.5 h-3.5 flex-shrink-0 ${hasActiveChild ? 'text-[#0077FF]' : 'text-slate-400'}`} />
                      <span className="flex-1 text-left">{group.label}</span>
                      <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} />
                    </button>
                    <div
                      className="overflow-hidden transition-[max-height] duration-200 ease-out"
                      style={{ maxHeight: isExpanded ? `${group.items.length * 36 + 8}px` : '0px' }}
                    >
                      <div className="ml-5 mt-0.5 mb-1 pl-3 border-l border-slate-100 space-y-0.5">
                        {group.items.map(item => {
                          const Icon = item.icon;
                          const active = isActivePage(item.path);
                          return (
                            <Link
                              key={item.path}
                              to={getPath(item.path)}
                              onClick={() => setIsOpen(false)}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[11px] font-medium transition-colors duration-150 ${
                                active
                                  ? 'text-[#0077FF] bg-[#0077FF]/6 font-semibold'
                                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                              }`}
                              data-testid={`side-tab-link-${item.path.slice(1)}`}
                            >
                              <Icon className={`w-3 h-3 flex-shrink-0 ${active ? 'text-[#0077FF]' : 'text-slate-400'}`} />
                              {item.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Security link */}
              <Link
                to={getPath('/security')}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors duration-150 ${
                  isActivePage('/security')
                    ? 'text-[#0077FF] bg-[#0077FF]/6 font-semibold'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
                data-testid="side-tab-link-security"
              >
                <Lock className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                Security
              </Link>
            </div>
          </div>

          {/* ════════ ZONE 3 — THIS PAGE ════════ */}
          {pageSections.length > 0 && (
            <>
              <div className="mx-4 border-t border-slate-100 mb-2" />
              <div className="px-4 flex-1">
                <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-2.5 px-1">
                  This Page
                </p>
                {currentPath === '/' ? (
                  /* ─── Grouped collapsible view for Homepage ─── */
                  <div className="space-y-0.5">
                    {HOME_SECTION_GROUPS.map((group) => {
                      const isExpanded = expandedPageGroups[group.key];
                      const hasActiveChild = group.items.some(s => activeSection === s.id);
                      return (
                        <div key={group.key}>
                          <button
                            onClick={() => togglePageGroup(group.key)}
                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-[11px] font-semibold transition-colors duration-150 ${
                              hasActiveChild
                                ? 'text-[#0077FF] bg-[#0077FF]/6'
                                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                            }`}
                            data-testid={`side-tab-page-group-${group.key}`}
                          >
                            <span className="flex-1">{group.label}</span>
                            <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} />
                          </button>
                          <div
                            className="overflow-hidden transition-[max-height] duration-200 ease-out"
                            style={{ maxHeight: isExpanded ? `${group.items.length * 32 + 8}px` : '0px' }}
                          >
                            <div className="ml-3 mt-0.5 mb-1 pl-3 border-l border-slate-100 space-y-0.5">
                              {group.items.map((section) => {
                                const isActive = activeSection === section.id;
                                return (
                                  <button
                                    key={section.id}
                                    onClick={() => scrollToSection(section.id)}
                                    className={`w-full flex items-center gap-2 px-2 py-1 rounded-md text-left text-[11px] transition-all duration-150 ${
                                      isActive
                                        ? 'text-[#0077FF] font-semibold bg-[#0077FF]/8'
                                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700 font-medium'
                                    }`}
                                    data-testid={`side-tab-section-${section.id}`}
                                  >
                                    {section.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Get Started remains as a standalone link */}
                    <button
                      onClick={() => scrollToSection('cta-section')}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-[11px] transition-all duration-150 ${
                        activeSection === 'cta-section'
                          ? 'bg-[#0077FF]/8 text-[#0077FF] font-semibold'
                          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700 font-medium'
                      }`}
                      data-testid="side-tab-section-cta-section"
                    >
                      Get Started
                    </button>
                  </div>
                ) : (
                  /* ─── Flat list for other pages ─── */
                  <div className="space-y-0.5">
                    {pageSections.map((section) => {
                      const isActive = activeSection === section.id;
                      return (
                        <button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-[11px] transition-all duration-150 ${
                            isActive
                              ? 'bg-[#0077FF]/8 text-[#0077FF] font-semibold border-l-2 border-[#0077FF] -ml-[2px] pl-[12px]'
                              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700 font-medium'
                          }`}
                          data-testid={`side-tab-section-${section.id}`}
                        >
                          {section.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ════════ CTA — Request Demo ════════ */}
          <div className="px-4 mt-3 pt-3 border-t border-slate-100">
            <button
              onClick={() => navigateTo('/contact')}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#FF7F00] hover:bg-[#E67200] transition-colors duration-150"
              data-testid="side-tab-request-demo"
            >
              <Mail className="w-4 h-4" />
              Request Demo
            </button>
          </div>

          {/* ════════ THE SHOW ════════ */}
          <div className="px-4 mt-2 pt-2 border-t border-slate-200">
            <Link
              to="/the-show"
              onClick={() => setIsOpen(false)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-[#0077FF] border border-[#0077FF]/20 bg-[#0077FF]/[0.04] hover:bg-[#0077FF]/10 hover:border-[#0077FF]/40 focus:ring-2 focus:ring-[#0077FF]/30 focus:outline-none active:bg-[#0077FF]/15 transition-all duration-150"
              data-testid="side-tab-the-show"
            >
              <PlayCircle className="w-4 h-4" />
              The Show
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
};

export default SideTab;
